import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { AlertTriangle, ArrowLeft, Link2, PackagePlus, Pencil, Plus, Split, Store, Trash2 } from 'lucide-react'
import {
  Alert, Card, EmptyState, Field, Input, Loading, Modal, SearchInput, Select, StatusPill,
  Textarea, Th, sortRows, useSort, useToast,
} from '@/components/ui'
import { AttachmentList, AuditTrail } from '@/components/docs/shared'
import { DocumentActions } from '@/components/docs/DocumentActions'
import { useAuth } from '@/lib/auth'
import { DeleteDocument } from '@/components/docs/DeleteDocument'
import { fetchLines, fetchOne, useGrns, useMaterials, usePeople, useSiteNames, useSites, useVendors } from '@/lib/data'
import { supabase } from '@/lib/supabase'
import { can, checkTolerance, isEditable, seesManySites, visibleSites } from '@/lib/domain'
import { useCompanySettings } from '@/lib/settings'
import { ddmmyyyy, dateTime, qty, rupees } from '@/lib/format'
import type { Grn, GrnLine, Material } from '@/lib/types'

const STATUSES = [
  { value: '', label: 'All statuses' },
  { value: 'SUBMITTED', label: 'Awaiting approval' },
  { value: 'APPROVED', label: 'Approved — stock updated' },
  { value: 'RETURNED', label: 'Returned for revision' },
  { value: 'REJECTED', label: 'Rejected' },
]

export function GrnList() {
  const { profile } = useAuth()
  const role = profile!.role
  const ho = seesManySites(role)
  // A Project Manager picking a colleague's site would see nothing, which
  // looks like a fault rather than a boundary. Offer him only his own.
  const [site, setSite] = useState(ho ? '' : (profile!.site_id ?? ''))
  const [status, setStatus] = useState('')
  const [term, setTerm] = useState('')
  // The screen names the person; grn.creator_email stays the identity that
  // canCorrect compares on.
  const people = usePeople()
  const { siteName } = useSiteNames()

  const { data: sites } = useSites()
  const mySites = useMemo(() => visibleSites(sites, role, profile!.id), [sites, role, profile])
  const { data, loading, error } = useGrns({ site_id: site || undefined, status: status ? [status] : undefined })

  const rows = useMemo(() => {
    const t = term.trim().toLowerCase()
      // Resolve the name ONCE per row. It is both what the column DRAWS and
      // what the column SORTS on — leaving the sort pointed at the hidden
      // email reorders the table in a way the reader cannot explain, and
      // leaving the search pointed at it means typing a name finds nothing.
    const named = data.map((g: any) => ({ ...g, entered_by: people.name({ email: g.creator_email }) }))
    if (!t) return named
    return named.filter((g: any) =>
      g.id.toLowerCase().includes(t) ||
      (g.challan_no ?? '').toLowerCase().includes(t) ||
      (g.po_id ?? '').toLowerCase().includes(t) ||
      g.entered_by.toLowerCase().includes(t))
  }, [data, term, people.index])

  const { sort, toggle } = useSort('received_date')
  const sorted = useMemo(() => sortRows(rows, sort, {
    received_date: 'date', challan_date: 'date',
  }), [rows, sort])

  return (
    <div className="space-y-4">
      <Card
        title="Goods Receipt Notes"
        subtitle="Recorded at site on delivery; stock moves only after the Head of Stores approves"
        actions={can(role, 'createGrn') && (
          <Link to="/grns/new" className="btn-primary btn-sm"><Plus className="h-4 w-4" /> Receive material</Link>
        )}
      >
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          <SearchInput value={term} onChange={setTerm} placeholder="Search GRN, challan number, PO…" />
          {ho && (
            <Select value={site} onChange={(e) => setSite(e.target.value)} placeholder="All sites"
                    options={mySites.map((s) => ({ value: s.id, label: s.site_name }))} />
          )}
          <Select value={status} onChange={(e) => setStatus(e.target.value)} options={STATUSES} />
        </div>
      </Card>

      <Card bodyClass="p-0 sm:p-0">
        {loading && <Loading />}
        {error && <div className="p-4"><EmptyState title="Could not load receipts" message={error} /></div>}
        {!loading && !error && rows.length === 0 && (
          <EmptyState icon={<PackagePlus className="h-9 w-9" />} title="No goods receipts yet"
                      message="When a vendor delivers, record it here so the stock is credited to your site." />
        )}
        {rows.length > 0 && (
          <div className="table-wrap mx-0">
            <table className="tbl">
              <thead>
                <tr>
                  <Th id="id" sort={sort} onSort={toggle}>GRN</Th><Th id="grn_type" sort={sort} onSort={toggle}>Type</Th><Th id="site_id" sort={sort} onSort={toggle}>Site</Th><Th id="po_id" sort={sort} onSort={toggle}>Against PO</Th>
                  <Th id="challan_no" sort={sort} onSort={toggle}>Challan</Th><Th id="received_date" sort={sort} onSort={toggle}>Received</Th><Th id="status" sort={sort} onSort={toggle}>Status</Th><Th id="entered_by" sort={sort} onSort={toggle}>Entered by</Th><th />
                </tr>
              </thead>
              <tbody>
                {sorted.map((g) => (
                  <tr key={g.id}>
                    <td>
                      <Link to={`/grns/${g.id}`} className="font-mono text-xs font-semibold text-oval-700 hover:underline">{g.id}</Link>
                    </td>
                    <td>{g.grn_type === 'ADHOC' ? <span className="pill-amber">Ad-hoc</span> : <span className="pill-grey">Against PO</span>}</td>
                    <td className="text-xs" title={g.site_id}>{siteName(g.site_id)}</td>
                    <td className="font-mono text-xs text-slate-500">{g.po_id ?? '—'}</td>
                    <td className="text-xs">{g.challan_no ?? '—'}</td>
                    <td className="text-xs">{ddmmyyyy(g.received_date)}</td>
                    <td><StatusPill status={g.status} /></td>
                    <td className="max-w-[180px] truncate text-xs text-slate-500">{g.entered_by}</td>
                    <td>{g.tolerance_breach && <AlertTriangle className="h-4 w-4 text-amber-500" aria-label="Tolerance exceeded" />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}

// ---------------------------------------------------------------------------
export function GrnDetail() {
  const { id = '' } = useParams()
  const { profile } = useAuth()
  const [grn, setGrn] = useState<Grn | null>(null)
  const [lines, setLines] = useState<GrnLine[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [nameOpen, setNameOpen] = useState(false)
  const [pickVendor, setPickVendor] = useState('')
  const [nameNote, setNameNote] = useState('')
  const [naming, setNaming] = useState(false)
  const [linkOpen, setLinkOpen] = useState(false)
  const [splitOpen, setSplitOpen] = useState(false)
  // 0222: the parts this receipt was split into, if any.
  const [parts, setParts] = useState<{ id: string; po_id: string | null }[]>([])
  const toast = useToast()
  const { siteName } = useSiteNames()

  const { data: materials } = useMaterials()
  const { data: vendors, refresh: refreshVendors } = useVendors()
  // The screen names the person; grn.creator_email stays the identity that
  // canCorrect compares on.
  const people = usePeople()
  const byId = useMemo(
    () => Object.fromEntries(materials.map((m) => [m.id, m])) as Record<string, Material>, [materials])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const header = await fetchOne<Grn>('grns', id)
      if (!header) throw new Error(`Goods receipt ${id} was not found, or you do not have access to it.`)
      setGrn(header)
      setLines(await fetchLines<GrnLine>('grn_line_items', 'grn_id', id))
      const { data: split } = await supabase.from('grns').select('id, po_id')
        .eq('split_from', id).order('id')
      setParts(split ?? [])
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the goods receipt')
    } finally { setLoading(false) }
  }, [id])

  useEffect(() => { void load() }, [load])

  if (loading) return <Loading label="Loading goods receipt…" />
  if (error || !grn) return <Alert tone="error">{error}</Alert>

  const vendor = vendors.find((v) => v.id === grn.vendor_id)
  const payable = lines.reduce((a, l) => a + l.quantity_accepted * l.conversion_factor * l.unit_rate, 0)

  const canCorrect = isEditable(grn.status) && grn.creator_email === profile!.email

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <Link to="/grns" className="btn-ghost btn-sm"><ArrowLeft className="h-4 w-4" /> All receipts</Link>
          <h1 className="font-mono text-base font-bold text-slate-800">{grn.id}</h1>
          <StatusPill status={grn.status} />
          {grn.grn_type === 'ADHOC' && <span className="pill-amber">Ad-hoc purchase</span>}
          {(grn as { adhoc_linked_at?: string }).adhoc_linked_at && (
            <span className="pill-blue" title="Entered at the gate as ad-hoc, linked to its purchase order afterwards. The audit trail below says who, when and why.">
              Linked to PO on {ddmmyyyy((grn as { adhoc_linked_at?: string }).adhoc_linked_at)}
            </span>
          )}
          {/* 0222: one delivery split across several purchase orders. */}
          {grn.split_from && (
            <Link to={`/grns/${grn.split_from}`} className="pill-blue hover:underline"
                  title="Part of one delivery that belonged to several purchase orders. The stock stays recorded against the original receipt.">
              Split from {grn.split_from}
            </Link>
          )}
          {parts.length > 0 && (
            <span className="pill-blue" title="This delivery belonged to several purchase orders, so it was split. The stock stays recorded against this receipt.">
              Split into{' '}
              {parts.map((p, i) => (
                <Fragment key={p.id}>
                  {i > 0 && ', '}
                  <Link to={`/grns/${p.id}`} className="hover:underline">{p.id}</Link>
                </Fragment>
              ))}
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {canCorrect && (
            <Link to={`/grns/${grn.id}/edit`} className="btn-primary btn-sm">
              <Pencil className="h-4 w-4" /> Correct and resubmit
            </Link>
          )}
          <DeleteDocument docType="GRN" docId={grn.id} status={grn.status}
                          creatorEmail={grn.creator_email} backTo="/grns" />
        </div>
      </div>

      {grn.tolerance_breach && (
        <Alert tone="warning" title="Acceptance tolerance exceeded">
          At least one line accepts more than the over-receipt tolerance (System Settings, 10% unless
          changed) above the outstanding purchase-order quantity.
          Check the highlighted rows before approving.
        </Alert>
      )}
      {grn.status === 'APPROVED' && (
        <Alert tone="success" title="Approved — inventory updated">
          The accepted quantity is now in the {siteName(grn.site_id)} inventory, and a vendor payable of{' '}
          <strong>{rupees(payable)}</strong> (before GST) has been raised for the accepted quantity only.
        </Alert>
      )}
      {grn.status === 'REJECTED' && (
        <Alert tone="error" title="Rejected — permanently stopped">{grn.rejection_reason ?? 'No reason recorded.'}</Alert>
      )}
      {grn.status === 'RETURNED' && (
        <Alert tone="warning" title="Returned for revision">{grn.return_reason ?? 'No note recorded.'}</Alert>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <Card title="Consignment" className="lg:col-span-2">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm sm:grid-cols-3">
            <D label="Site" value={siteName(grn.site_id)} />
            <D label="Against purchase order" value={grn.po_id ?? 'None (ad-hoc)'} />
            <D label="Vendor" value={vendor?.company_name ?? grn.vendor_id ?? '—'} />
            <D label="Received on" value={ddmmyyyy(grn.received_date)} />
            <D label="Posts to" value={ddmmyyyy((grn as { posting_date?: string }).posting_date)} />
            <D label="Challan" value={`${grn.challan_no ?? '—'} (${ddmmyyyy(grn.challan_date)})`} />
            <D label="E-way bill" value={grn.eway_bill_no ?? '—'} />
            <D label="Vehicle" value={grn.vehicle_no ?? '—'} />
            <D label="Entered by" value={people.name({ email: grn.creator_email })} />
            <D label="Approved by" value={grn.approver_email ? people.name({ email: grn.approver_email }) : '—'} />
          </dl>
          {(grn as { payment_mode?: string }).payment_mode && (
            <div className="mt-4">
              {(grn as { payment_mode?: string }).payment_mode === 'PETTY_CASH' ? (
                <Alert tone="success" title="Paid at site from petty cash">
                  The money left the site when the material arrived. A cash payment was
                  recorded against this receipt and the bill is closed — nothing is owing.
                </Alert>
              ) : (
                <Alert tone="info" title="Credit purchase">
                  A payable was raised. The vendor will bill for it, and it is matched and
                  paid like any other purchase.
                </Alert>
              )}
            </div>
          )}

          {(grn as { posting_date?: string }).posting_date
            && (grn as { posting_date?: string }).posting_date !== grn.received_date && (
            <div className="mt-3">
              <Alert tone="warning" title="This receipt posts to a later month than it arrived">
                The lorry came on <strong>{ddmmyyyy(grn.received_date)}</strong>, but that
                month was already closed, so the figures land on{' '}
                <strong>{ddmmyyyy((grn as { posting_date?: string }).posting_date)}</strong>.
                <p className="mt-2">
                  The document keeps its true date. The <strong>stock also moves on the
                  posting date</strong>, so this material will not appear in an Inventory
                  &ldquo;as at&rdquo; figure for the earlier month — that is deliberate, and
                  it is what keeps stock value and the accounts agreeing.
                </p>
              </Alert>
            </div>
          )}

          {vendor?.is_placeholder && (
            <div className="mt-3">
              <Alert tone="warning" title="Nobody has named the shop this came from yet">
                <p>
                  This purchase sits against <strong>{vendor.company_name}</strong>, a
                  placeholder. The bill is attached below — open it, find out who the
                  vendor actually was, add him to the vendor master if he is not there
                  already, then move this purchase onto him.
                </p>
                {can(profile!.role, 'identifyAdhocVendor') ? (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Link to="/masters" className="btn-secondary btn-sm">
                      <Plus className="mr-1 h-4 w-4" /> Masters → Vendors
                    </Link>
                    <button className="btn-primary btn-sm" onClick={() => setNameOpen(true)}>
                      <Store className="mr-1 h-4 w-4" /> Name the vendor
                    </button>
                  </div>
                ) : (
                  <p className="mt-2 text-xs">
                    A Procurement Executive or the Procurement Manager does this.
                  </p>
                )}
              </Alert>
            </div>
          )}

          {/* Link to its real purchase order, 29-09-2026 (migration 0182).
              Only an approved ad-hoc receipt bought on credit: a petty-cash one
              was a genuine local purchase and is already settled. */}
          {grn.grn_type === 'ADHOC' && grn.status === 'APPROVED'
            && (grn as { payment_mode?: string }).payment_mode !== 'PETTY_CASH'
            && can(profile!.role, 'linkAdhocToPo') && (
            <div className="mt-3">
              <Alert tone="info" title="Was this actually delivered against a purchase order?">
                <p>
                  If Head Office or the site has since found that this lorry was delivering
                  against an IMS purchase order, link it. The order then shows the material
                  as received, and the payable moves to the order&apos;s vendor at the
                  order&apos;s rate.
                </p>
                <p className="mt-2">
                  If one lorry brought material for <strong>several</strong> orders, split it
                  across them: each order gets its own part of this receipt.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button className="btn-primary btn-sm" onClick={() => setLinkOpen(true)}>
                    <Link2 className="mr-1 h-4 w-4" /> Link to a purchase order
                  </button>
                  <button className="btn-secondary btn-sm" onClick={() => setSplitOpen(true)}>
                    <Split className="mr-1 h-4 w-4" /> Split across purchase orders
                  </button>
                </div>
              </Alert>
            </div>
          )}

          {grn.inspection_notes && (
            <div className="mt-4 rounded-lg bg-slate-50 px-3 py-2 text-sm">
              <p className="text-xs font-semibold text-slate-500">Inspection notes</p>
              <p className="text-slate-700">{grn.inspection_notes}</p>
            </div>
          )}
        </Card>

        <Card title="Decision" subtitle="Approval credits the accepted quantity to site stock">
          <DocumentActions docType="GRN" docId={grn.id} status={grn.status} onDone={load} className="flex flex-col gap-2" />
          {grn.status !== 'SUBMITTED' && <p className="text-sm text-slate-500">No decision is pending.</p>}
        </Card>
      </div>

      <Card title="Materials received">
        <div className="table-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>#</th><th>Material</th>
                <th className="num">Challan</th><th className="num">Counted</th>
                <th className="num">Accepted</th><th className="num">Rejected</th>
                <th className="num">Rate</th><th className="num">Payable</th>
                <th>Rejection reason</th><th />
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => {
                const m = byId[l.material_id]
                return (
                  <Fragment key={l.id}>
                  <tr className={!l.within_tolerance ? 'bg-amber-50' : ''}>
                    <td className="text-xs text-slate-400">{l.sl_no}</td>
                    <td className="text-sm font-medium">{m?.item_name ?? l.material_id}</td>
                    <td className="num text-xs">{qty(l.quantity_challan)} {l.purchase_uom}</td>
                    <td className="num">{qty(l.quantity_received)} {l.stock_uom}</td>
                    <td className="num font-semibold text-emerald-700">{qty(l.quantity_accepted)}</td>
                    <td className="num font-semibold text-red-600">{qty(l.quantity_rejected)}</td>
                    <td className="num text-xs">{rupees(l.unit_rate)}/{l.purchase_uom}</td>
                    <td className="num text-xs">{rupees(l.quantity_accepted * l.conversion_factor * l.unit_rate)}</td>
                    <td className="max-w-[200px] truncate text-xs text-slate-500">{l.rejection_reason ?? '—'}</td>
                    <td>{!l.within_tolerance && <AlertTriangle className="h-4 w-4 text-amber-500" />}</td>
                  </tr>
                  {/* The Head of Stores approves this receipt. On measured timber
                      he must be able to see HOW the volume was arrived at, not
                      just the volume — otherwise he is signing a bare number. */}
                  {Array.isArray(l.timber_tally) && l.timber_tally.length > 0 && (
                    <tr>
                      <td />
                      <td colSpan={9} className="pb-3">
                        <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-amber-800">
                          Timber tally
                        </span>
                        <span className="flex flex-wrap gap-1.5">
                          {l.timber_tally.map((t: any, k: number) => (
                            <span key={k} className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-900">
                              {qty(t.pieces)} NO · {t.width_in}&quot;×{t.thickness_in}&quot; · {qty(t.length_ft)} ft
                              <strong className="ml-1">= {Number(t.cft).toFixed(3)}</strong>
                            </span>
                          ))}
                          <span className="rounded bg-amber-200 px-2 py-1 text-[11px] font-bold text-amber-900">
                            {l.timber_tally.reduce((a: number, t: any) => a + Number(t.cft || 0), 0).toFixed(3)} {l.stock_uom}
                          </span>
                        </span>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-right text-sm">
          Payable to the vendor (accepted only, before GST): <strong>{rupees(payable)}</strong>
        </p>
      </Card>

      <AttachmentList docType="GRN" docId={grn.id} />

      <AuditTrail docType="GRN" docId={grn.id} />

      {linkOpen && (
        <LinkToPoModal grn={grn} lines={lines} byId={byId}
                       vendorIsPlaceholder={!!vendor?.is_placeholder}
                       onClose={() => setLinkOpen(false)}
                       onDone={async () => { setLinkOpen(false); refreshVendors(); await load() }} />
      )}
      {splitOpen && (
        <SplitAcrossPosModal grn={grn} lines={lines} byId={byId}
                             vendorIsPlaceholder={!!vendor?.is_placeholder}
                             onClose={() => setSplitOpen(false)}
                             onDone={async () => { setSplitOpen(false); refreshVendors(); await load() }} />
      )}

      {nameOpen && (
        <Modal open onClose={() => setNameOpen(false)}
               title={`Name the vendor behind ${grn.id}`}
               subtitle="This moves the purchase, the payable and any cash payment onto the real shop"
               footer={<>
                 <button className="btn-secondary" onClick={() => setNameOpen(false)} disabled={naming}>
                   Cancel
                 </button>
                 <button className="btn-primary" disabled={naming || !pickVendor}
                         onClick={async () => {
                           setNaming(true)
                           const { error: e } = await supabase.rpc('rpc_reassign_adhoc_vendor', {
                             p_grn_id: grn.id, p_vendor_id: pickVendor, p_remarks: nameNote || null,
                           })
                           setNaming(false)
                           if (e) { toast.error(e.message); return }
                           toast.success('The purchase now sits against the real vendor.')
                           setNameOpen(false); setPickVendor(''); setNameNote('')
                           refreshVendors()
                           await load()
                         }}>
                   {naming ? 'Moving…' : 'Move it onto him'}
                 </button>
               </>}>
          <Alert tone="info" title="What this changes">
            <ul className="mt-1 space-y-1 text-sm">
              <li>The goods receipt points at the real vendor</li>
              <li>The payable behind it moves too, so his balance is right</li>
              <li>Any petty-cash payment moves with it</li>
              <li>The audit trail records who named him, and when</li>
            </ul>
            <p className="mt-2 text-xs">
              It does <strong>not</strong> change quantities, rates or dates. Nothing about
              what arrived is touched.
            </p>
          </Alert>

          <div className="mt-3 space-y-3">
            <Field label="Which vendor was it?" required
                   hint="Not on the list? Add him under Masters → Vendors first, then come back.">
              <Select value={pickVendor} onChange={(e) => setPickVendor(e.target.value)}
                      placeholder="Choose the real vendor…"
                      options={vendors
                        .filter((v) => !v.is_placeholder)
                        .map((v) => ({ value: v.id, label: v.company_name }))} />
            </Field>
            <Field label="How was he identified?"
                   hint="Worth a line — the next person reading the audit trail will want to know">
              <Input value={nameNote} onChange={(e) => setNameNote(e.target.value)}
                     placeholder="e.g. Name and GSTIN printed on the cash memo" />
            </Field>
          </div>
        </Modal>
      )}
    </div>
  )
}

const D = ({ label, value }: { label: string; value: string }) => (
  <div>
    <dt className="text-xs font-semibold text-slate-500">{label}</dt>
    <dd className="mt-0.5 break-words text-slate-800">{value}</dd>
  </div>
)

// ---------------------------------------------------------------------------
// Link an approved ad-hoc receipt to the purchase order it was really
// delivered against. 29-09-2026, migration 0182.
//
// The screen only PREVIEWS. Every rule shown here - same site, Vendor
// Acknowledged or Partially Received, same vendor unless the receipt is still
// against OTHERS, every accepted material on the order and within what is
// pending - is enforced again by rpc_link_adhoc_grn_to_po, which also refuses
// a payable that has been invoiced, paid or credited, and a closed month. The
// preview is there so the person sees WHY before pressing the button.
// ---------------------------------------------------------------------------
function LinkToPoModal({ grn, lines, byId, vendorIsPlaceholder, onClose, onDone }: {
  grn: Grn
  lines: GrnLine[]
  byId: Record<string, Material>
  vendorIsPlaceholder: boolean
  onClose: () => void
  onDone: () => Promise<void>
}) {
  const toast = useToast()
  const { data: vendors } = useVendors()
  // 0184: the same System Setting the GRN screen and rpc_link_adhoc_grn_to_po read.
  const { grnTolerancePct } = useCompanySettings()
  const vendorName = (vid: string | null | undefined) =>
    vendors.find((v) => v.id === vid)?.company_name ?? vid ?? '—'

  const [pos, setPos] = useState<any[] | null>(null)
  const [poId, setPoId] = useState('')
  const [poLines, setPoLines] = useState<any[] | null>(null)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  // The orders that could have taken this delivery.
  useEffect(() => {
    let live = true
    void (async () => {
      let q = supabase.from('purchase_orders')
        .select('id, vendor_id, status, date_created, total_amount')
        .eq('site_id', grn.site_id)
        .in('status', ['VENDACK', 'PARTIALLY_RECEIVED'])
      // A receipt that already names a real vendor can only go to his orders.
      if (!vendorIsPlaceholder && grn.vendor_id) q = q.eq('vendor_id', grn.vendor_id)
      const { data, error } = await q.order('date_created', { ascending: false })
      if (!live) return
      if (error) { toast.error(error.message); setPos([]); return }
      setPos(data ?? [])
    })()
    return () => { live = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grn.id])

  useEffect(() => {
    if (!poId) { setPoLines(null); return }
    let live = true
    setPoLines(null)
    void (async () => {
      const { data } = await supabase.from('po_line_items').select('*').eq('po_id', poId).order('sl_no')
      if (live) setPoLines(data ?? [])
    })()
    return () => { live = false }
  }, [poId])

  // One row per material on the receipt, set against the order.
  const check = useMemo(() => {
    if (!poLines) return []
    const byMat = new Map<string, { accepted: number; cf: number; rate: number }>()
    for (const l of lines) {
      const cur = byMat.get(l.material_id)
      byMat.set(l.material_id, {
        accepted: (cur?.accepted ?? 0) + Number(l.quantity_accepted),
        cf: Number(l.conversion_factor),
        rate: Number(l.unit_rate),
      })
    }
    return [...byMat.entries()].map(([mid, r]) => {
      const matches = poLines.filter((p) => p.material_id === mid)
      const p = matches[0]
      let problem: string | null = null
      let note: string | null = null
      if (matches.length === 0) problem = r.accepted > 0 ? 'Not on this order' : null
      else if (matches.length > 1) problem = 'On the order twice'
      else if (Math.abs(Number(p.conversion_factor) - r.cf) > 0.0001) problem = 'Different conversion factor'
      else if (Number(p.quantity_remaining) <= 0.0005) problem = 'Nothing pending on the order'
      else {
        // 0183: the same 10% a receipt entered against the order is allowed
        // (checkTolerance). Over, but within it, is fine - and said so.
        const tol = checkTolerance(r.accepted, Number(p.quantity_remaining), grnTolerancePct)
        if (!tol.withinTolerance) problem = `Most it can take is ${qty(tol.ceiling)} (pending + ${grnTolerancePct}%)`
        else if (r.accepted > Number(p.quantity_remaining) + 0.0005) note = `Yes — ${tol.variancePct}% over, within ${grnTolerancePct}%`
      }
      return {
        mid, name: byId[mid]?.item_name ?? mid, accepted: r.accepted, oldRate: r.rate,
        pending: p ? Number(p.quantity_remaining) : null,
        newRate: p ? Number(p.unit_rate) : null,
        uom: p?.purchase_uom ?? byId[mid]?.purchase_uom ?? '',
        problem, note,
      }
    })
  }, [poLines, lines, byId, grnTolerancePct])

  const blocked = check.some((c) => c.problem)
  const chosen = pos?.find((p) => p.id === poId)

  async function link() {
    if (reason.trim().length < 10) {
      toast.error('Write how you know this delivery belongs to that order - at least a short sentence.'); return
    }
    setBusy(true)
    const { error } = await supabase.rpc('rpc_link_adhoc_grn_to_po', {
      p_grn_id: grn.id, p_po_id: poId, p_reason: reason.trim(),
    })
    setBusy(false)
    if (error) { toast.error(error.message); return }
    toast.success(`${grn.id} is now against ${poId}.`)
    await onDone()
  }

  return (
    <Modal open wide onClose={onClose}
           title={`Link ${grn.id} to a purchase order`}
           subtitle="For a delivery entered as ad-hoc that was really against an IMS order"
           footer={<>
             <button className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
             <button className="btn-primary" onClick={() => void link()}
                     disabled={busy || !poId || !poLines || blocked}>
               {busy ? 'Linking…' : 'Link it to this order'}
             </button>
           </>}>
      <Alert tone="info" title="What this changes">
        <ul className="mt-1 space-y-1 text-sm">
          <li>The order shows this material as received, and moves to Partially received or Closed</li>
          <li>The receipt and its payable move to the order&apos;s vendor</li>
          <li>The payable is re-priced at the order&apos;s rate and GST, and any advance on the order is applied</li>
          <li>The audit trail on both documents records who, when, why, and the old figures</li>
        </ul>
        <p className="mt-2 text-xs">
          It does <strong>not</strong> move stock again, and it cannot be undone. It is refused if the
          payable has already been invoiced, paid or credited, or if the receipt&apos;s month has been
          closed.
        </p>
      </Alert>

      <div className="mt-3 space-y-3">
        <Field label="Which purchase order?" required
               hint={vendorIsPlaceholder
                 ? 'Orders for this site that the vendor has acknowledged, or that are partly received.'
                 : `Only ${vendorName(grn.vendor_id)}'s orders for this site - this receipt already names him.`}>
          {pos === null ? <Loading label="Finding orders…" /> : pos.length === 0 ? (
            <p className="text-sm text-slate-500">
              No acknowledged or partly received order at this site
              {vendorIsPlaceholder ? '' : ` for ${vendorName(grn.vendor_id)}`}. Nothing to link to.
            </p>
          ) : (
            <Select value={poId} onChange={(e) => setPoId(e.target.value)} placeholder="Choose the order…"
                    options={pos.map((p) => ({
                      value: p.id,
                      label: `${p.id} · ${vendorName(p.vendor_id)} · ${ddmmyyyy(p.date_created)} · ${p.status === 'VENDACK' ? 'Acknowledged' : 'Partly received'}`,
                    }))} />
          )}
        </Field>

        {poId && !poLines && <Loading label="Reading the order…" />}
        {poId && poLines && (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Material</th><th className="num">Accepted here</th><th className="num">Pending on order</th>
                  <th className="num">Rate now</th><th className="num">Order rate</th><th>Fits?</th>
                </tr>
              </thead>
              <tbody>
                {check.map((c) => (
                  <tr key={c.mid} className={c.problem ? 'bg-red-50' : ''}>
                    <td className="text-sm font-medium">{c.name}</td>
                    <td className="num">{qty(c.accepted)}</td>
                    <td className="num">{c.pending == null ? '—' : qty(c.pending)}</td>
                    <td className="num text-xs">{rupees(c.oldRate)}</td>
                    <td className="num text-xs">{c.newRate == null ? '—' : `${rupees(c.newRate)}/${c.uom}`}</td>
                    <td className={`text-xs font-semibold ${c.problem ? 'text-red-700' : 'text-emerald-700'}`}>
                      {c.problem ?? c.note ?? 'Yes'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {poId && poLines && blocked && (
          <Alert tone="error" title="This delivery does not fit that order">
            Every accepted material must be on the order once, with the same conversion factor, and
            no more than {grnTolerancePct}% over what the order still has pending — the same limit as a
            receipt entered against the order. Choose another order, or leave this receipt as ad-hoc.
          </Alert>
        )}
        {chosen && !blocked && poLines && (
          <p className="text-xs text-slate-600">
            The payable will move to <strong>{vendorName(chosen.vendor_id)}</strong> at the order&apos;s rates.
          </p>
        )}

        <Field label="How do you know it belongs to this order?" required
               hint="Kept permanently in the audit trail of the receipt and the order">
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. Challan 1733 quotes OVPO-20260921-072; confirmed with the vendor by phone on 29-09-2026." />
        </Field>
      </div>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// Split an approved ad-hoc receipt across several purchase orders. 02-11-2026,
// migration 0222. One lorry, material for two (or more) orders: the first
// order keeps this receipt, each other order gets a part (<number>-2, -3 ...),
// and anything not allocated stays ad-hoc on a part of its own. Each part is
// then linked by the unchanged rpc_link_adhoc_grn_to_po, so the rules below
// are exactly the link's, checked per order - and again by the database.
// ---------------------------------------------------------------------------
function SplitAcrossPosModal({ grn, lines, byId, vendorIsPlaceholder, onClose, onDone }: {
  grn: Grn
  lines: GrnLine[]
  byId: Record<string, Material>
  vendorIsPlaceholder: boolean
  onClose: () => void
  onDone: () => Promise<void>
}) {
  const toast = useToast()
  const { data: vendors } = useVendors()
  const { grnTolerancePct } = useCompanySettings()
  const vendorName = (vid: string | null | undefined) =>
    vendors.find((v) => v.id === vid)?.company_name ?? vid ?? '—'

  const [pos, setPos] = useState<any[] | null>(null)
  const [rows, setRows] = useState<{ poId: string; qty: Record<string, string> }[]>(
    [{ poId: '', qty: {} }, { poId: '', qty: {} }])
  const [poLines, setPoLines] = useState<Record<string, any[]>>({})
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  // What the receipt accepted, per material.
  const accepted = useMemo(() => {
    const m = new Map<string, { qty: number; cf: number }>()
    for (const l of lines) {
      if (Number(l.quantity_accepted) <= 0) continue
      const cur = m.get(l.material_id)
      m.set(l.material_id, { qty: (cur?.qty ?? 0) + Number(l.quantity_accepted), cf: Number(l.conversion_factor) })
    }
    return m
  }, [lines])
  const mats = [...accepted.keys()]

  useEffect(() => {
    let live = true
    void (async () => {
      let q = supabase.from('purchase_orders')
        .select('id, vendor_id, status, date_created')
        .eq('site_id', grn.site_id)
        .in('status', ['VENDACK', 'PARTIALLY_RECEIVED'])
      if (!vendorIsPlaceholder && grn.vendor_id) q = q.eq('vendor_id', grn.vendor_id)
      const { data, error } = await q.order('date_created', { ascending: false })
      if (!live) return
      if (error) { toast.error(error.message); setPos([]); return }
      setPos(data ?? [])
    })()
    return () => { live = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grn.id])

  const n = (s: string | undefined) => Number(s ?? '') || 0
  const allocatedOf = (mid: string, except = -1) =>
    rows.reduce((a, r, i) => (i === except ? a : a + n(r.qty[mid])), 0)

  async function choose(i: number, poId: string) {
    let pl = poLines[poId]
    if (poId && !pl) {
      const { data } = await supabase.from('po_line_items').select('*').eq('po_id', poId).order('sl_no')
      pl = data ?? []
      setPoLines((m) => ({ ...m, [poId]: pl }))
    }
    // Prefill: what the order still has pending, out of what is not yet allocated.
    const qty: Record<string, string> = {}
    for (const mid of mats) {
      const p = (pl ?? []).filter((x) => x.material_id === mid)
      if (p.length !== 1) continue
      const free = Math.max(0, accepted.get(mid)!.qty - allocatedOf(mid, i))
      const take = Math.min(free, Number(p[0].quantity_remaining))
      if (take > 0) qty[mid] = String(Math.round(take * 1000) / 1000)
    }
    setRows((rs) => rs.map((r, j) => (j === i ? { poId, qty } : r)))
  }

  // Every problem, per order and per material - the link's own rules.
  const problems: string[] = []
  rows.forEach((r) => {
    if (!r.poId) return
    const pl = poLines[r.poId]
    if (!pl) return
    let any = false
    for (const mid of mats) {
      const q = n(r.qty[mid])
      if (q <= 0) continue
      any = true
      const name = byId[mid]?.item_name ?? mid
      const p = pl.filter((x) => x.material_id === mid)
      if (p.length === 0) { problems.push(`${name} is not on ${r.poId}.`); continue }
      if (p.length > 1) { problems.push(`${name} is on ${r.poId} twice.`); continue }
      if (Math.abs(Number(p[0].conversion_factor) - accepted.get(mid)!.cf) > 0.0001) {
        problems.push(`${name}: ${r.poId} uses a different conversion factor.`); continue
      }
      const pending = Number(p[0].quantity_remaining)
      if (pending <= 0.0005) { problems.push(`${name}: ${r.poId} has nothing pending.`); continue }
      const tol = checkTolerance(q, pending, grnTolerancePct)
      if (!tol.withinTolerance) problems.push(`${name}: ${r.poId} can take at most ${qty(tol.ceiling)} (pending ${qty(pending)} + ${grnTolerancePct}%).`)
    }
    if (!any) problems.push(`${r.poId} has no quantity against it.`)
  })
  for (const mid of mats) {
    const over = allocatedOf(mid) - accepted.get(mid)!.qty
    if (over > 0.0005) problems.push(`${byId[mid]?.item_name ?? mid}: ${qty(over)} more allocated than the receipt accepted.`)
  }
  const chosen = rows.filter((r) => r.poId)
  const ids = chosen.map((r) => r.poId)
  if (new Set(ids).size !== ids.length) problems.push('The same order is chosen twice.')
  const remainder = mats
    .map((mid) => ({ mid, left: accepted.get(mid)!.qty - allocatedOf(mid) }))
    .filter((x) => x.left > 0.0005)
  if (chosen.length === 1 && remainder.length === 0) {
    problems.push('Everything goes to one order - use "Link to a purchase order" instead.')
  }
  const ready = chosen.length >= 1 && problems.length === 0
    && chosen.every((r) => !!poLines[r.poId]) && reason.trim().length >= 10

  async function split() {
    setBusy(true)
    const allocations = chosen.map((r) => ({
      po_id: r.poId,
      lines: mats.filter((mid) => n(r.qty[mid]) > 0).map((mid) => ({ material_id: mid, qty: n(r.qty[mid]) })),
    }))
    const { data, error } = await supabase.rpc('rpc_split_adhoc_grn_to_pos', {
      p_grn_id: grn.id, p_allocations: allocations, p_reason: reason.trim(),
    })
    setBusy(false)
    if (error) { toast.error(error.message); return }
    toast.success(`Split: ${String(data)}`)
    await onDone()
  }

  let partNo = 1
  return (
    <Modal open wide onClose={onClose}
           title={`Split ${grn.id} across purchase orders`}
           subtitle="One delivery that belonged to several IMS orders"
           footer={<>
             <button className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
             <button className="btn-primary" onClick={() => void split()} disabled={busy || !ready}>
               {busy ? 'Splitting…' : 'Split and link'}
             </button>
           </>}>
      <Alert tone="info" title="What this does">
        <ul className="mt-1 space-y-1 text-sm">
          <li>The <strong>first</strong> order keeps {grn.id}; each other order gets a part, {grn.id}-2, -3 …</li>
          <li>Each part is linked to its order exactly as &ldquo;Link to a purchase order&rdquo; does: the order shows it received, and its payable moves to the order&apos;s vendor at the order&apos;s rate</li>
          <li>Anything not allocated stays ad-hoc on a part of its own, at the rate entered at the gate</li>
          <li>The <strong>stock does not move</strong> - the total received is unchanged</li>
        </ul>
        <p className="mt-2 text-xs">It cannot be undone. It is refused if the payable has been invoiced, paid or credited, if the month is closed, or if Accounts has already taken the receipt into Tally.</p>
      </Alert>

      <div className="mt-3 space-y-3">
        {pos === null ? <Loading label="Finding orders…" /> : pos.length === 0 ? (
          <p className="text-sm text-slate-500">No acknowledged or partly received order at this site{vendorIsPlaceholder ? '' : ` for ${vendorName(grn.vendor_id)}`}. Nothing to split across.</p>
        ) : rows.map((r, i) => {
          const label = i === 0 ? `Order 1 - keeps ${grn.id}` : `Order ${i + 1} - becomes ${grn.id}-${++partNo}`
          return (
            <div key={i} className="rounded-lg border border-slate-200 p-3">
              <div className="flex flex-wrap items-end gap-2">
                <Field label={label} className="min-w-[260px] flex-1">
                  <Select value={r.poId} onChange={(e) => void choose(i, e.target.value)} placeholder="Choose the order…"
                          options={pos.map((p) => ({
                            value: p.id,
                            label: `${p.id} · ${vendorName(p.vendor_id)} · ${ddmmyyyy(p.date_created)} · ${p.status === 'VENDACK' ? 'Acknowledged' : 'Partly received'}`,
                          }))} />
                </Field>
                {rows.length > 1 && (
                  <button className="btn-ghost btn-sm text-red-700" title="Take this order out"
                          onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              {r.poId && poLines[r.poId] && (
                <div className="table-wrap mt-2">
                  <table className="tbl">
                    <thead>
                      <tr><th>Material</th><th className="num">Pending on order</th><th className="num">Goes to this order</th></tr>
                    </thead>
                    <tbody>
                      {mats.map((mid) => {
                        const p = poLines[r.poId].filter((x) => x.material_id === mid)
                        return (
                          <tr key={mid}>
                            <td className="text-sm">{byId[mid]?.item_name ?? mid}</td>
                            <td className="num">{p.length === 1 ? `${qty(p[0].quantity_remaining)} ${p[0].purchase_uom ?? ''}` : 'not on order'}</td>
                            <td className="num">
                              <Input type="number" min={0} step="any" className="w-28 text-right"
                                     disabled={p.length !== 1}
                                     value={r.qty[mid] ?? ''}
                                     onChange={(e) => setRows((rs) => rs.map((x, j) =>
                                       j === i ? { ...x, qty: { ...x.qty, [mid]: e.target.value } } : x))} />
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )
        })}

        {pos && pos.length > 0 && (
          <button className="btn-secondary btn-sm" onClick={() => setRows((rs) => [...rs, { poId: '', qty: {} }])}>
            <Plus className="mr-1 h-4 w-4" /> Add another order
          </button>
        )}

        <div className="rounded-lg bg-slate-50 px-3 py-2 text-sm">
          <p className="text-xs font-semibold text-slate-500">Accepted on {grn.id}, and what is left unallocated</p>
          <ul className="mt-1 space-y-0.5">
            {mats.map((mid) => (
              <li key={mid}>
                {byId[mid]?.item_name ?? mid}: accepted <strong>{qty(accepted.get(mid)!.qty)}</strong>,
                allocated {qty(allocatedOf(mid))}
                {accepted.get(mid)!.qty - allocatedOf(mid) > 0.0005 && (
                  <span className="text-amber-700"> - {qty(accepted.get(mid)!.qty - allocatedOf(mid))} stays ad-hoc</span>
                )}
              </li>
            ))}
          </ul>
        </div>

        {problems.length > 0 && (
          <Alert tone="error" title="Not yet">
            <ul className="space-y-0.5 text-sm">{problems.map((p) => <li key={p}>{p}</li>)}</ul>
          </Alert>
        )}

        <Field label="How do you know it belongs to these orders?" required
               hint="Kept permanently in the audit trail of every receipt and order involved">
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. Challan 2211 lists OVPO-20261025-169 and -170, 100 each; confirmed with the vendor." />
        </Field>
      </div>
    </Modal>
  )
}
